/**
 * Maps each SVG layer type to the minimum map zoom level at which it becomes visible.
 *
 * @type {Record<string, number>}
 */
export const SVG_LAYER_VISIBILITY = {
  // Label layers — visible only at zoom 16+ (NHS20-8038)
  StreetNames:   16,
  LotNumbers:    16,
  AmenityNames:  16,
  BuildingNames: 16,

  // All other layers — no zoom restriction; visible whenever the Interactive Site Map overlay is active
  LotPolygons:  0,
  Grass:        0,
  Street:       0,
  Amenity:      0,
  Buildings:    0,
  Pool:         0,
  Golf:         0,
  Wetland:      0,
  Landscaping:  0,
  Walkway:      0,
  Water:        0,
  Sand:         0,
  Curbs:        0,
  LotGroup:     0,
};

/**
 * Minimum map zoom level below which no Interactive Site Map layers are rendered.
 * Matches the minimum zoom enforced by the Google Maps widget per NHS20-8038
 * ("Users cannot zoom out beyond zoom level 11/12"). Acts as a global safety gate.
 * @type {number}
 */
export const SITE_MAP_MIN_ZOOM = 12;

/**
 * Valid values for viewState.selectedTab.
 * @type {string}
 */
export const TAB_FLOOR_PLANS = 'floorPlans';

/**
 * @type {string}
 */
export const TAB_QUICK_MOVE_INS = 'quickMoveIns';

/**
 * Floor Plan Detail view — Lot & Elevation tab (default).
 * @type {string}
 */
export const TAB_LOT_AND_ELEVATION = 'lotAndElevation';

/**
 * Floor Plan Detail view — Floor Plan Configuration tab.
 * @type {string}
 */
export const TAB_FLOOR_PLAN_CONFIG = 'floorPlanConfiguration';

/**
 * Valid values for viewState.activeView.
 * @type {string}
 */
export const VIEW_LISTING = 'listing';

/**
 * Lot Map Selection view — plan picker shown when an Available lot has more than one
 * buildable floor plan. See STORE_DESIGN.md's View State Machine section.
 * @type {string}
 */
export const VIEW_LOT_MAP_SELECTION = 'lotMapSelection';

/**
 * Floor Plan Detail view — floor plan detail with Lot & Elevation / Configuration tabs, per
 * STORE_DESIGN.md's View State Machine section. plugins/floorPlanDetail.plugin.js owns the tabs
 * plus the plan's layout image — desktop-only, in the secondary panel overlay while the
 * Configuration tab is active; mobile behind a "View Floor Plan" expand screen instead, since
 * there's no secondary panel there.
 * @type {string}
 */
export const VIEW_FLOOR_PLAN_DETAIL = 'floorPlanDetail';

/**
 * Housing Unit Detail view — QMI or Model home detail, per STORE_DESIGN.md's View State Machine
 * section. Content for this view is rendered in the shared panel by feature plugins.
 * @type {string}
 */
export const VIEW_HOUSING_UNIT_DETAIL = 'housingUnitDetail';

/**
 * Sold Full List view — mobile-only full-screen listing of every Sold Home Card, reached via the
 * "See All" button shown once there are more than 10 (NHS20-8826). Owned by
 * plugins/listView.plugin.js, same as VIEW_LISTING — see STORE_DESIGN.md's View State Machine
 * section.
 * @type {string}
 */
export const VIEW_SOLD_FULL_LIST = 'soldFullList';

/**
 * Mobile-only cap on the in-panel Sold listing (NHS20-8826: "Display a maximum of 10 sold home
 * cards in the bottom sheet"). Desktop has no cap here — the whole point of the See All button
 * is that mobile's bottom sheet doesn't have room to show everything.
 * @type {number}
 */
export const MAX_SOLD_HOMES_MOBILE = 10;
